import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { ZIndex } from "src/ui/constants";

export const BarRow = styled.div``;
export const BarSection = styled.div``;
export const Handle = styled.div``;

interface RootProps {
	visible: boolean;
}

export const Root = styled.aside<RootProps>`
	${({ theme, visible }) => {
		return css`
			background-image: repeating-linear-gradient(
				-45deg,
				#333333,
				#333333 18px,
				#c58000 18px,
				#c58000 24px
			);
			background-repeat: no-repeat;
			background-position: right top;
			background-attachment: fixed;
			display: flex;
			flex-direction: column;
			flex-wrap: nowrap;
			justify-content: center;
			align-items: flex-start;
			width: 100%;
			height: ${visible ? "100%" : "0"};
			z-index: ${ZIndex.DeveloperPanel};

			${BarRow} {
				color: ${theme.colors.typography.selectionForeground};
				display: ${visible ? "flex" : "none"};
				justify-content: center;
				align-items: center;
				padding: 5px 8px 0px 8px;
				gap: 24px;

				${BarSection} {
					display: flex;
					justify-content: center;
					align-items: center;
					gap: 8px;
				}
			}

			${Handle} {
				${visible
					? { backgroundColor: "#333333", bottom: "-8px" }
					: { backgroundColor: "#c58000", bottom: "-4px" }}
				display: inline-block;
				position: relative;
				left: 50%;
				transform: translateX(-50%);
				padding: 0px;
				margin: 0px;
				width: 80px;
				height: 8px;
				border-bottom-left-radius: 8px;
				border-bottom-right-radius: 8px;
				cursor: pointer;
			}
		`;
	}}
`;

export const StockEditorOverlay = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.7);
	backdrop-filter: blur(4px);
	z-index: ${ZIndex.DeveloperWidgetViewer};
	display: flex;
	justify-content: center;
	align-items: center;
`;

interface StockEditorPanelProps {
	panelWidth: number;
}

export const StockEditorPanel = styled.div<StockEditorPanelProps>`
	background: #1a1a1a;
	border: 2px solid #c58000;
	border-radius: 8px;
	width: ${({ panelWidth }) => panelWidth}px;
	max-width: 95vw;
	max-height: 80vh;
	display: flex;
	flex-direction: column;
	color: #eee;
	font-family: monospace;
	font-size: 12px;
	position: relative;
`;

export const ResizeHandle = styled.div`
	position: absolute;
	top: 0;
	right: -4px;
	width: 8px;
	height: 100%;
	cursor: ew-resize;
	z-index: 1;

	&:hover,
	&:active {
		background: rgba(197, 128, 0, 0.3);
	}
`;

export const StockEditorHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 12px 16px;
	border-bottom: 1px solid #c58000;
	gap: 8px;

	h3 {
		margin: 0;
		color: #c58000;
		font-size: 14px;
	}

	button {
		padding: 4px 12px;
		border: 1px solid #555;
		background: #333;
		color: #eee;
		cursor: pointer;
		border-radius: 4px;
		font-family: monospace;
		font-size: 12px;

		&:hover {
			background: #444;
		}
	}
`;

export const StockEditorActions = styled.div`
	display: flex;
	gap: 8px;
`;

export const SelectionActions = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 6px 16px;
	background: #222;
	border-bottom: 1px solid #333;
	font-size: 11px;
	color: #aaa;

	button {
		padding: 2px 8px;
		border: 1px solid #555;
		background: #333;
		color: #eee;
		cursor: pointer;
		border-radius: 3px;
		font-family: monospace;
		font-size: 11px;

		&:hover {
			background: #444;
		}
	}

	span {
		color: #c58000;
		font-weight: bold;
	}
`;

export const StockEditorBody = styled.div`
	overflow-y: auto;
	flex: 1;
	padding: 8px;
`;

export const StockTable = styled.table`
	width: 100%;
	border-collapse: collapse;

	th {
		position: sticky;
		top: 0;
		background: #222;
		color: #c58000;
		text-align: left;
		padding: 6px 8px;
		border-bottom: 2px solid #c58000;
		font-weight: bold;
		white-space: nowrap;
		cursor: pointer;
		user-select: none;

		&:hover {
			background: #2a2a2a;
		}
	}

	td {
		padding: 4px 8px;
		border-bottom: 1px solid #333;
		white-space: nowrap;
	}
`;

export const EditableCell = styled.td`
	cursor: text;

	input {
		background: #111;
		border: 1px solid #555;
		color: #eee;
		padding: 2px 4px;
		width: 80px;
		font-family: monospace;
		font-size: 12px;
		border-radius: 2px;

		&:focus {
			border-color: #c58000;
			outline: none;
		}
	}
`;

export const SortIndicator = styled.span`
	margin-left: 4px;
	font-size: 10px;
	opacity: 0.8;
`;

interface StockRowProps {
	inStock: boolean;
	selected?: boolean;
}

export const StockRow = styled.tr<StockRowProps>`
	opacity: ${({ inStock }) => (inStock ? 1 : 0.35)};
	cursor: pointer;
	user-select: none;

	td {
		text-decoration: ${({ inStock }) => (inStock ? "none" : "line-through")};
		background: ${({ selected }) =>
			selected ? "rgba(197, 128, 0, 0.2)" : "transparent"};
	}

	&:hover td {
		background: ${({ selected }) =>
			selected ? "rgba(197, 128, 0, 0.3)" : "#2a2a2a"};
	}
`;

export const InStockLabel = styled.label`
	display: flex;
	align-items: center;
	gap: 4px;
	cursor: pointer;
	font-size: 11px;
	color: #aaa;
	white-space: nowrap;

	input[type="checkbox"] {
		cursor: pointer;
		accent-color: #c58000;
	}
`;

export const ProductTag = styled.span`
	color: #888;
	font-size: 11px;
`;

export const ResetButton = styled.button`
	padding: 4px 12px;
	border: 1px solid #555;
	background: #333;
	color: #eee;
	cursor: pointer;
	border-radius: 4px;
	font-family: monospace;
	font-size: 12px;

	&:hover {
		background: #444;
	}
`;

export const WidgetViewer = styled.aside`
	background-color: rgba(0, 0, 0, 0.4);
	backdrop-filter: blur(4px);
	padding: 8px;
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	z-index: ${ZIndex.DeveloperWidgetViewer};
`;
